import { useCallback, useEffect, useRef, useState } from "react";

/*
 * Plays song preview clips, one at a time, with soft fades.
 *
 * - Fades use the Web Audio API (a gain node) instead of audio.volume, because
 *   iPhone/iPad Safari ignores audio.volume. Browsers without Web Audio still
 *   play the clip, just without fades or visuals.
 * - While playing, the music's loudness is written to the CSS variable --level
 *   (0 to 1) on `levelTargetRef`, so the page can react to the music.
 *
 * Usage:
 *   const player = usePreviewPlayer({ levelTargetRef });
 *   player.play(song.preview, "bucthudau"); player.pause(); player.stop();
 *   player.play(track.preview, "track-2", { onEnd: playNextTrack });
 *     -> onEnd runs when the clip finishes on its own (not when paused or
 *        replaced), e.g. to roll on to the next track of an EP/album
 *   player.playingId  -> id of the clip playing now (or null)
 *   player.currentId  -> id of the loaded clip (also while paused)
 *   player.progress   -> 0 to 1 through the loaded clip
 */
const FADE_SECONDS = 1.2; // fade at the start and end of a clip
const QUICK_FADE_SECONDS = 0.25; // fade when pausing or resuming mid-clip

export default function usePreviewPlayer({ levelTargetRef } = {}) {
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const gainRef = useRef(null);
  const analyserRef = useRef(null);
  const clipRef = useRef(null); // { src, start, duration } of the loaded clip
  const fadingOutRef = useRef(false);
  const pauseTimerRef = useRef(null);
  const meterFrameRef = useRef(null);
  const levelRef = useRef(0);
  const onEndRef = useRef(null); // called when the loaded clip finishes by itself

  const [playingId, setPlayingId] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [progress, setProgress] = useState(0);

  // These only read refs, so they never need to change
  const clipStart = useCallback(() => clipRef.current?.start || 0, []);
  const clipEnd = useCallback(() => {
    const clip = clipRef.current;
    return clip?.duration ? (clip.start || 0) + clip.duration : audioRef.current?.duration;
  }, []);

  const rampTo = useCallback((value, seconds) => {
    const gain = gainRef.current;
    if (!gain) return;
    const now = ctxRef.current.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(value, now + seconds);
  }, []);

  const setLevel = (value) =>
    levelTargetRef?.current?.style.setProperty("--level", value);

  const reset = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    clearTimeout(pauseTimerRef.current);
    audio.pause();
    audio.currentTime = clipStart();
    fadingOutRef.current = false;
    setPlayingId(null);
    setProgress(0);
  }, [clipStart]);

  // The clip played to its end: stop, then let the caller carry on (once)
  const finish = useCallback(() => {
    const onEnd = onEndRef.current;
    onEndRef.current = null;
    reset();
    onEnd?.();
  }, [reset]);

  const onTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    const end = clipEnd();
    if (!end || Number.isNaN(end)) return;
    const start = clipStart();
    const remaining = end - audio.currentTime;
    setProgress(Math.min(Math.max((audio.currentTime - start) / (end - start), 0), 1));

    // Start the fade-out as the clip nears its end
    if (!fadingOutRef.current && remaining <= FADE_SECONDS) {
      fadingOutRef.current = true;
      rampTo(0, Math.max(remaining, 0.05));
    }
    if (remaining <= 0) finish();
  }, [clipStart, clipEnd, rampTo, finish]);

  // One <audio> element, reused for every clip
  const getAudio = () => {
    if (!audioRef.current) {
      const audio = new Audio();
      audio.crossOrigin = "anonymous";
      audio.preload = "metadata";
      audio.addEventListener("timeupdate", onTimeUpdate);
      audio.addEventListener("ended", finish);
      audioRef.current = audio;
    }
    return audioRef.current;
  };

  // Built on first play, since browsers only allow audio after a tap/click
  const getGain = () => {
    if (gainRef.current) return gainRef.current;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    const ctx = new AudioCtx();
    const source = ctx.createMediaElementSource(getAudio());
    const gain = ctx.createGain();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(gain).connect(analyser).connect(ctx.destination);
    ctxRef.current = ctx;
    gainRef.current = gain;
    analyserRef.current = analyser;
    return gain;
  };

  // Measures loudness every frame and eases --level toward it (rises quickly,
  // falls slowly), stopping once the music is paused and quiet.
  const startMeter = () => {
    const analyser = analyserRef.current;
    if (!analyser || meterFrameRef.current) return;
    const samples = new Uint8Array(analyser.fftSize);

    const tick = () => {
      const audio = audioRef.current;
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (let i = 0; i < samples.length; i++) {
        const v = (samples[i] - 128) / 128;
        sum += v * v;
      }
      const loudness = Math.sqrt(sum / samples.length);
      // Ignore the quietest bits so loud and soft parts feel clearly different
      const target = audio.paused ? 0 : Math.min(1, Math.max(0, (loudness - 0.05) * 3));
      const current = levelRef.current;
      const level = current + (target - current) * (target > current ? 0.3 : 0.06);
      levelRef.current = level;
      setLevel(level.toFixed(3));

      if (audio.paused && level < 0.005) {
        levelRef.current = 0;
        setLevel("0");
        meterFrameRef.current = null;
        return;
      }
      meterFrameRef.current = requestAnimationFrame(tick);
    };
    meterFrameRef.current = requestAnimationFrame(tick);
  };

  const play = (clip, id, { onEnd } = {}) => {
    const audio = getAudio();
    clearTimeout(pauseTimerRef.current);
    onEndRef.current = onEnd || null;

    // A different clip: load it (stops whatever was playing)
    if (clipRef.current?.src !== clip.src) {
      audio.pause();
      clipRef.current = clip;
      audio.src = clip.src;
      audio.currentTime = clip.start || 0;
      setProgress(0);
    }
    setCurrentId(id);

    const end = clipEnd();
    if (audio.currentTime < clipStart() || (end && audio.currentTime >= end)) {
      audio.currentTime = clipStart();
    }
    const fromStart = audio.currentTime - clipStart() < 0.1;

    const gain = getGain();
    if (gain) {
      ctxRef.current.resume();
      // Always fade in from silence. (Don't read gain.value here: on the
      // first play it still reports the default of 1, which skipped the fade.)
      const now = ctxRef.current.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(
        1,
        now + (fromStart ? FADE_SECONDS : QUICK_FADE_SECONDS)
      );
    }
    fadingOutRef.current = false;

    setPlayingId(id);
    audio
      .play()
      .then(startMeter)
      .catch(() => setPlayingId(null));
  };

  const pause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setPlayingId(null);
    if (!gainRef.current) {
      audio.pause();
      return;
    }
    rampTo(0, QUICK_FADE_SECONDS);
    pauseTimerRef.current = setTimeout(() => audio.pause(), QUICK_FADE_SECONDS * 1000);
  };

  // Stop: pause (with the quick fade), forget the onEnd roll-on, and rewind,
  // so nothing shows as loaded any more
  const stop = () => {
    const audio = audioRef.current;
    onEndRef.current = null;
    pause();
    setCurrentId(null);
    setProgress(0);
    if (!audio) return;
    setTimeout(() => {
      // (unless something started playing again meanwhile)
      if (audio.paused) audio.currentTime = clipStart();
    }, QUICK_FADE_SECONDS * 1000 + 50);
  };

  // Stop and release audio when the page is left
  useEffect(
    () => () => {
      clearTimeout(pauseTimerRef.current);
      cancelAnimationFrame(meterFrameRef.current);
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.removeEventListener("timeupdate", onTimeUpdate);
        audio.removeEventListener("ended", finish);
      }
      if (ctxRef.current) ctxRef.current.close();
    },
    [onTimeUpdate, finish]
  );

  return { play, pause, stop, playingId, currentId, progress };
}
